import React from 'react';
import { Box, Typography, Card, CardContent, Divider, Chip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

function formatCents(cents) {
  if (cents == null) return '—';
  const negative = cents < 0;
  const formatted = `$${(Math.abs(cents) / 100).toFixed(2)}`;
  return negative ? `-${formatted}` : formatted;
}

function LineItem({ label, value, color, bold }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: bold ? 600 : 400 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: bold ? 600 : 400, color: color || 'inherit' }}>
        {typeof value === 'number' ? formatCents(value) : value}
      </Typography>
    </Box>
  );
}

export default function FinancialPreview({ preview }) {
  if (!preview || !preview.summary) {
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">No financial preview available</Typography>
        </CardContent>
      </Card>
    );
  }

  const { operation, summary } = preview;

  if (summary.error) {
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography color="error" variant="body2">{summary.error}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem', color: 'text.secondary' }}>
          Financial Impact — {operation}
        </Typography>

        {/* Upgrade/downgrade preview */}
        {(operation === 'upgrade' || operation === 'downgrade') && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip label={summary.current_plan?.name} size="small"
                color={summary.current_plan?.is_trial ? 'info' : 'default'} variant="outlined" />
              <ArrowForwardIcon fontSize="small" color="action" />
              <Chip label={summary.target_plan?.name} size="small" color={summary.is_upgrade ? 'success' : 'warning'} />
            </Box>

            {summary.in_trial ? (
              <>
                {/* Trial user upgrading/changing plan */}
                <Box sx={{ p: 1.5, bgcolor: 'info.50', borderRadius: 1, mb: 1.5 }}>
                  <Typography variant="body2" fontWeight={600} color="info.dark">
                    You're on a free trial — {summary.trial_days_remaining} {summary.trial_days_remaining === 1 ? 'day' : 'days'} remaining
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    No charges have been made to your account yet.
                  </Typography>
                </Box>
                <LineItem label="Amount paid so far" value={0} color="success.main" />
                <LineItem label="New plan price" value={summary.target_plan?.price_cents} />
                <Divider sx={{ my: 1 }} />
                {summary.charge_description && (
                  <Typography variant="body2" sx={{ mb: 1, fontSize: '0.85rem' }}>
                    {summary.charge_description}
                  </Typography>
                )}
                <LineItem
                  label="First charge"
                  value={summary.first_charge_cents}
                  color="primary"
                  bold
                />
                <LineItem label="Due today" value={summary.charge_due_today_cents ?? 0} color="success.main" />
              </>
            ) : (
              <>
                {/* Active subscriber upgrading/downgrading */}
                <LineItem label="Current plan price" value={summary.current_plan?.price_cents} />
                <LineItem label="New plan price" value={summary.target_plan?.price_cents} />
                <Divider sx={{ my: 1 }} />
                <LineItem label="Days remaining in period" value={`${summary.days_remaining} / ${summary.total_days}`} />
                <LineItem label="Unused credit" value={summary.unused_credit_cents} color="success.main" />
                <LineItem label="New charge (prorated)" value={summary.new_charge_cents} />
                <Divider sx={{ my: 1 }} />
                <LineItem
                  label="Net charge"
                  value={summary.prorated_amount_cents}
                  color={summary.prorated_amount_cents > 0 ? 'error.main' : 'success.main'}
                  bold
                />
              </>
            )}

            {/* Account balance */}
            {preview.account_balance && preview.account_balance.total_paid_cents > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Account: {formatCents(preview.account_balance.total_paid_cents)} paid
                  {preview.account_balance.total_credits_cents > 0 && ` · ${formatCents(preview.account_balance.total_credits_cents)} credits`}
                </Typography>
              </>
            )}
          </>
        )}

        {/* Cancel preview */}
        {operation === 'cancel' && (
          <>
            <LineItem label="Plan" value={summary.plan?.name} />
            <LineItem label="Days remaining" value={`${summary.days_remaining} / ${summary.total_days}`} />
            <Divider sx={{ my: 1 }} />
            <LineItem label="Refund amount" value={summary.refund_amount_cents} color="success.main" bold />
            {summary.cancel_immediately && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                Subscription will be canceled immediately
              </Typography>
            )}
          </>
        )}

        {/* Discount preview */}
        {operation === 'discount' && (
          <>
            <LineItem label="Original price" value={summary.original_price_per_period_cents} />
            <LineItem label="Discount per period" value={summary.discount_per_period_cents} color="success.main" />
            <LineItem label="New price per period" value={summary.new_price_per_period_cents} bold />
            <Divider sx={{ my: 1 }} />
            <LineItem label="Periods affected" value={summary.periods_affected} />
            <LineItem label="Total savings" value={summary.total_savings_cents} color="success.main" bold />
          </>
        )}

        {/* Rebundle preview */}
        {operation === 'rebundle' && (
          <>
            <LineItem label="Individual total" value={summary.individual_total_cents} />
            <LineItem label="Bundle price" value={summary.bundle_price_cents} />
            <Divider sx={{ my: 1 }} />
            <LineItem label="Savings per period" value={summary.savings_per_period_cents} color="success.main" bold />
            <LineItem label="Savings %" value={`${summary.savings_percentage}%`} color="success.main" />
          </>
        )}

        {/* Enroll preview */}
        {operation === 'enroll' && (
          <>
            <LineItem label="Plan" value={summary.plan?.name} />
            <LineItem label="Interval" value={summary.interval} />
            {summary.trial_days > 0 && <LineItem label="Trial period" value={`${summary.trial_days} days`} />}
            <Divider sx={{ my: 1 }} />
            <LineItem label="First charge" value={summary.first_charge_cents} bold />
          </>
        )}

        {/* Override preview */}
        {operation === 'override' && (
          <>
            {summary.plan && <LineItem label="Plan" value={summary.plan?.name} />}
            <LineItem label="Charge" value="$0.00 (free)" color="success.main" bold />
            <LineItem label="Time-limited" value={summary.is_time_limited ? 'Yes' : 'No (indefinite)'} />
            {summary.expires_at && <LineItem label="Expires" value={new Date(summary.expires_at).toLocaleDateString()} />}
          </>
        )}
      </CardContent>
    </Card>
  );
}
